import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VinScanner from '../components/VinScanner.js';
import { api } from '../services/api-client.js';

function formatPrice(cents: number) {
  if (!cents) return '$0';
  return '$' + (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

interface VehicleGotcha {
  title: string;
  description: string;
  severity: 'critical' | 'major' | 'minor';
  category: string;
  typical_mileage: string | null;
  typical_repair_cost: string | null;
  frequency: string;
}

interface AuctionResult {
  vin: string;
  vin_decode: {
    year: number; make: string; model: string; trim: string | null;
    engine: string | null; transmission: string | null; drivetrain: string | null;
    fuel_type: string | null; body_style: string | null;
  };
  market: {
    street_price_cents: number; median_price_cents: number;
    low_price_cents: number; high_price_cents: number;
    sample_size: number; market_supply: string; ai_summary: string;
  };
  profit_calculator: {
    street_value_cents: number; estimated_repairs_cents: number;
    target_buy_price_cents: number; estimated_profit_cents: number;
    notes: string;
  };
  gotchas?: {
    issues: VehicleGotcha[];
    risk_score: number;
    summary: string;
    cached: boolean;
  };
}

interface ScanHistoryEntry {
  vin: string;
  title: string;
  scannedAt: string;
  streetPriceCents: number;
  maxBuyCents: number;
  profitCents: number;
  riskScore: number | null;
  saved: boolean; // true = auction list, false = temporary scan
  notes: string;  // dealer's own notes about this car
}

const SCAN_HISTORY_KEY = 'cdos-scan-history';
const MAX_HISTORY = 100;
const DECAY_MS = 24 * 60 * 60 * 1000; // 24 hours

function loadScanHistory(): ScanHistoryEntry[] {
  try {
    const all: ScanHistoryEntry[] = JSON.parse(localStorage.getItem(SCAN_HISTORY_KEY) || '[]');
    const now = Date.now();
    // Keep saved items forever, unsaved items decay after 24h
    const active = all.filter(h => h.saved || (now - new Date(h.scannedAt).getTime()) < DECAY_MS);
    // Persist cleanup
    if (active.length < all.length) {
      localStorage.setItem(SCAN_HISTORY_KEY, JSON.stringify(active));
    }
    return active;
  } catch { return []; }
}

function saveScanToHistory(result: AuctionResult) {
  const history = loadScanHistory();
  const existing = history.find(h => h.vin === result.vin);
  const wasSaved = existing?.saved ?? false;
  const filtered = history.filter(h => h.vin !== result.vin);
  const title = [result.vin_decode.year, result.vin_decode.make, result.vin_decode.model, result.vin_decode.trim]
    .filter(Boolean).join(' ');
  filtered.unshift({
    vin: result.vin,
    title,
    scannedAt: new Date().toISOString(),
    streetPriceCents: result.market.street_price_cents,
    maxBuyCents: result.profit_calculator.target_buy_price_cents,
    profitCents: result.profit_calculator.estimated_profit_cents,
    riskScore: result.gotchas?.risk_score ?? null,
    saved: wasSaved, // preserve saved status on re-scan
    notes: existing?.notes ?? '',
  });
  localStorage.setItem(SCAN_HISTORY_KEY, JSON.stringify(filtered.slice(0, MAX_HISTORY)));
}

function toggleSaved(vin: string): ScanHistoryEntry[] {
  const history = loadScanHistory();
  const entry = history.find(h => h.vin === vin);
  if (entry) entry.saved = !entry.saved;
  localStorage.setItem(SCAN_HISTORY_KEY, JSON.stringify(history));
  return history;
}

function removeScan(vin: string): ScanHistoryEntry[] {
  const history = loadScanHistory().filter(h => h.vin !== vin);
  localStorage.setItem(SCAN_HISTORY_KEY, JSON.stringify(history));
  return history;
}

function updateNote(vin: string, notes: string): ScanHistoryEntry[] {
  const history = loadScanHistory();
  const entry = history.find(h => h.vin === vin);
  if (entry) entry.notes = notes;
  localStorage.setItem(SCAN_HISTORY_KEY, JSON.stringify(history));
  return history;
}

export default function AuctionScanPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AuctionResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [scanHistory, setScanHistory] = useState<ScanHistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    setScanHistory(loadScanHistory());
  }, []);

  const handleVinScanned = useCallback(async (vin: string) => {
    setError('');
    setLoading(true);
    setResult(null);

    try {
      const res = await api.post<AuctionResult>('/vehicles/auction/lookup', { vin });
      if (res.success && res.data) {
        setResult(res.data);
        saveScanToHistory(res.data);
        const updated = loadScanHistory();
        setScanHistory(updated);
        setNoteText(updated.find(h => h.vin === res.data!.vin)?.notes ?? '');
      } else {
        setError(res.error ?? 'Lookup failed');
      }
    } catch {
      setError('Network error — check your connection');
    } finally {
      setLoading(false);
    }
  }, []);

  const copyVin = useCallback((vin: string) => {
    navigator.clipboard.writeText(vin).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, []);

  const title = result?.vin_decode
    ? [result.vin_decode.year, result.vin_decode.make, result.vin_decode.model, result.vin_decode.trim].filter(Boolean).join(' ')
    : '';

  return (
    <div className="page">
      <h2 className="text-2xl font-bold text-center mb-2">Auction Scanner</h2>
      <p className="text-surface-500 text-center text-sm mb-6">
        Scan a VIN to get instant market intel + profit breakdown
      </p>

      {/* VIN Scanner + History */}
      {!result && !loading && (
        <>
          <VinScanner onVinScanned={handleVinScanned} />

          {/* Auction List + Recent Scans */}
          {scanHistory.length > 0 && (() => {
            const savedList = scanHistory.filter(h => h.saved);
            const recentList = scanHistory.filter(h => !h.saved);

            const renderEntry = (entry: ScanHistoryEntry) => (
              <div key={entry.vin} className="card p-3 flex items-center gap-3">
                <button
                  onClick={() => handleVinScanned(entry.vin)}
                  className="flex-1 text-left hover:opacity-80 transition-opacity"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{entry.title}</p>
                      <p className="text-xs text-surface-500 font-mono mt-0.5">{entry.vin}</p>
                      {entry.notes && (
                        <p className="text-xs text-surface-400 mt-1 italic truncate">{entry.notes}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      {entry.streetPriceCents > 0 && (
                        <p className="text-sm font-bold text-green-400">{formatPrice(entry.streetPriceCents)}</p>
                      )}
                      {entry.maxBuyCents > 0 && (
                        <p className="text-[10px] text-yellow-400">Max {formatPrice(entry.maxBuyCents)}</p>
                      )}
                      {entry.riskScore !== null && (
                        <p className={`text-[10px] font-medium ${
                          entry.riskScore >= 7 ? 'text-red-400' :
                          entry.riskScore >= 4 ? 'text-orange-400' :
                          'text-yellow-400'
                        }`}>Risk {entry.riskScore}/10</p>
                      )}
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => {
                    if (entry.saved) {
                      setScanHistory(toggleSaved(entry.vin));
                    } else {
                      setScanHistory(removeScan(entry.vin));
                    }
                  }}
                  className="shrink-0 p-1.5 rounded-lg hover:bg-surface-600/50 transition-colors text-surface-500 hover:text-surface-300"
                  title={entry.saved ? 'Remove from list' : 'Dismiss'}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );

            return (
              <div className="mt-6 space-y-4">
                {/* Auction List — saved items */}
                {savedList.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                      <span className="text-sm font-semibold text-yellow-400">Auction List ({savedList.length})</span>
                    </div>
                    <div className="space-y-2">
                      {savedList.map(renderEntry)}
                    </div>
                  </div>
                )}

                {/* Recent scans — auto-decay in 24h */}
                {recentList.length > 0 && (
                  <div>
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className="flex items-center justify-between w-full text-left text-sm text-surface-400 mb-2"
                    >
                      <span className="font-medium">Recent Scans ({recentList.length})</span>
                      <svg className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showHistory && (
                      <div className="space-y-2">
                        {recentList.slice(0, 20).map(renderEntry)}
                        <p className="text-[10px] text-surface-600 text-center pt-1">
                          Unsaved scans disappear after 24 hours
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-16">
          <div className="w-10 h-10 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-surface-300 font-semibold">Researching market...</p>
          <p className="text-surface-500 text-sm mt-1">Pulling prices from 4 sources</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-center mt-4">
          <p className="text-red-400 mb-3">{error}</p>
          <button onClick={() => { setError(''); setResult(null); }} className="btn-primary px-6">
            Try Again
          </button>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4 mt-4">
          {/* Vehicle Info */}
          <div className="card p-4">
            <h3 className="text-xl font-bold mb-1">{title}</h3>
            <div className="flex flex-wrap gap-2 text-xs text-surface-400">
              {result.vin_decode.engine && <span>{result.vin_decode.engine}</span>}
              {result.vin_decode.transmission && <span>| {result.vin_decode.transmission}</span>}
              {result.vin_decode.drivetrain && <span>| {result.vin_decode.drivetrain}</span>}
              {result.vin_decode.fuel_type && <span>| {result.vin_decode.fuel_type}</span>}
            </div>
            <button
              onClick={() => copyVin(result.vin)}
              className="flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg bg-surface-700/50 hover:bg-surface-600/50 transition-colors group"
            >
              <span className="font-mono tracking-wider text-sm text-surface-300">{result.vin}</span>
              <span className={`text-[10px] font-medium transition-colors ${copied ? 'text-green-400' : 'text-surface-500 group-hover:text-surface-300'}`}>
                {copied ? 'Copied!' : 'Copy'}
              </span>
            </button>
          </div>

          {/* Profit Calculator — the main event */}
          <div className="card p-5 border-2 border-brand-700 bg-brand-900/10">
            <h3 className="font-semibold text-sm text-brand-400 mb-4">Profit Calculator</h3>

            {/* Cheapest listed */}
            {result.market.low_price_cents > 0 && (
              <div className="bg-surface-700/30 rounded-xl p-2.5 mb-4 flex items-center justify-between">
                <span className="text-surface-400 text-xs">Cheapest one listed now</span>
                <span className="text-green-400 font-bold text-lg">{formatPrice(result.market.low_price_cents)}</span>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center">
                <p className="text-green-400 font-bold text-xl">{formatPrice(result.profit_calculator.street_value_cents)}</p>
                <p className="text-surface-500 text-[10px] uppercase tracking-wider mt-1">Private Party</p>
              </div>
              <div className="text-center">
                <p className="text-yellow-400 font-bold text-xl">{formatPrice(result.profit_calculator.target_buy_price_cents)}</p>
                <p className="text-surface-500 text-[10px] uppercase tracking-wider mt-1">Max All-In</p>
              </div>
              <div className="text-center">
                <p className="text-white font-bold text-xl">{formatPrice(result.profit_calculator.estimated_profit_cents)}</p>
                <p className="text-surface-500 text-[10px] uppercase tracking-wider mt-1">Est. Profit</p>
              </div>
            </div>

            <div className="bg-surface-700/50 rounded-xl p-3 text-xs text-surface-300 leading-relaxed">
              {result.profit_calculator.notes}
            </div>
          </div>

          {/* Vehicle Gotchas */}
          {result.gotchas && result.gotchas.issues.length > 0 && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm text-red-400">Known Issues — This Vehicle</h3>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  result.gotchas.risk_score >= 7 ? 'bg-red-900/50 text-red-400' :
                  result.gotchas.risk_score >= 4 ? 'bg-orange-900/50 text-orange-400' :
                  'bg-yellow-900/50 text-yellow-400'
                }`}>
                  Risk: {result.gotchas.risk_score}/10
                </span>
              </div>

              <p className="text-xs text-surface-400 mb-3">{result.gotchas.summary}</p>

              <div className="space-y-2">
                {result.gotchas.issues.map((issue, i) => (
                  <div
                    key={i}
                    className={`rounded-xl p-3 border-l-4 ${
                      issue.severity === 'critical'
                        ? 'bg-red-900/10 border-red-500'
                        : issue.severity === 'major'
                        ? 'bg-orange-900/10 border-orange-500'
                        : 'bg-yellow-900/10 border-yellow-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <span className="font-semibold text-sm text-white">{issue.title}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ml-2 shrink-0 uppercase ${
                        issue.severity === 'critical' ? 'bg-red-900/50 text-red-400' :
                        issue.severity === 'major' ? 'bg-orange-900/50 text-orange-400' :
                        'bg-yellow-900/50 text-yellow-400'
                      }`}>
                        {issue.severity}
                      </span>
                    </div>
                    <p className="text-xs text-surface-300 mt-1 leading-relaxed">{issue.description}</p>
                    <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-surface-500">
                      {issue.typical_mileage && <span>@ {issue.typical_mileage}</span>}
                      {issue.typical_repair_cost && <span>Repair: {issue.typical_repair_cost}</span>}
                      <span>{issue.frequency}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clean record */}
          {result.gotchas && result.gotchas.issues.length === 0 && result.gotchas.summary && (
            <div className="card p-4 border border-green-800/50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-900/30 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-green-400">Clean Record</h3>
                  <p className="text-xs text-surface-400 mt-0.5">{result.gotchas.summary}</p>
                </div>
              </div>
            </div>
          )}

          {/* Market Data */}
          {result.market.sample_size > 0 && (
            <div className="card p-4">
              <h3 className="font-semibold text-sm text-brand-400 mb-3">Market Data</h3>
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="text-center">
                  <p className="text-green-400 font-bold text-sm">{formatPrice(result.market.low_price_cents)}</p>
                  <p className="text-surface-500 text-[10px]">Low</p>
                </div>
                <div className="text-center">
                  <p className="text-brand-400 font-bold text-sm">{formatPrice(result.market.street_price_cents)}</p>
                  <p className="text-surface-500 text-[10px]">Street</p>
                </div>
                <div className="text-center">
                  <p className="text-blue-400 font-bold text-sm">{formatPrice(result.market.median_price_cents)}</p>
                  <p className="text-surface-500 text-[10px]">Median</p>
                </div>
                <div className="text-center">
                  <p className="text-yellow-400 font-bold text-sm">{formatPrice(result.market.high_price_cents)}</p>
                  <p className="text-surface-500 text-[10px]">High</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-surface-500">
                <span>{result.market.sample_size} listings found</span>
                <span className="capitalize">Supply: {result.market.market_supply}</span>
              </div>
              {result.market.ai_summary && (
                <p className="text-xs text-surface-400 mt-3 leading-relaxed">{result.market.ai_summary}</p>
              )}
            </div>
          )}

          {/* Quick Notes */}
          <div className="card p-3">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onBlur={() => {
                if (result) setScanHistory(updateNote(result.vin, noteText));
              }}
              placeholder="Add a note... (e.g. 'Row 5, runs rough, check trans')"
              rows={2}
              className="w-full bg-transparent text-sm text-surface-200 placeholder-surface-600 resize-none outline-none"
            />
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <button
              onClick={() => {
                // Auto-save note when saving to auction list
                if (noteText && result) updateNote(result.vin, noteText);
                setScanHistory(toggleSaved(result.vin));
              }}
              className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-colors ${
                scanHistory.find(h => h.vin === result.vin)?.saved
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-700'
                  : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
              }`}
            >
              <svg className="w-4 h-4" fill={scanHistory.find(h => h.vin === result.vin)?.saved ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              {scanHistory.find(h => h.vin === result.vin)?.saved ? 'On Auction List' : 'Save to Auction List'}
            </button>
            <button
              onClick={() => navigate('/add', { state: { vin: result.vin, vinDecode: result.vin_decode } })}
              className="btn-primary w-full"
            >
              Add to Inventory
            </button>
            <button
              onClick={() => { setResult(null); setError(''); }}
              className="btn-secondary w-full"
            >
              Scan Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
